/**
 * Brand Safety API seam.
 *
 * The single place that knows how the Brand Safety endpoints are reached. The slice thunks call these
 * wrappers; nothing else does. Every endpoint is live, so the thunks call the real API directly and a
 * failure surfaces as a real error (there is no mock fallback).
 */

import arClient from '../../util/api_client';

/**
 * Endpoint paths (host already ends in `/api/v1`, so these are relative to that). Kept here so the BE
 * dev can confirm every route in one place. `{postTableId}` / `{campaign}` / `{crd}` are substituted
 * by the builders.
 */
/**
 * Default flagged-posts page size for the single-influencer report (ep9). The BE caps pageSize at 100
 * and defaults to 25 when only `page` is sent; we send both so the response always carries a
 * `pagination` block (the endpoint omits it entirely when neither param is passed).
 */
export const BS_REPORT_PAGE_SIZE = 25;

/**
 * Campaign flaggedPosts (doc #4) is a FLAT, cursor-paginated feed: one `limit` knob (default 20, cap 100)
 * plus an opaque `cursor` resume token. We request the max page so client-side filter/sort see a
 * substantial batch; "Load more" fetches the next page via the previous response's `nextCursor`.
 */
export const BS_FLAGGED_POSTS_LIMIT = 100;

/**
 * Hard cap the BE puts on `detections[]` in one detection-review request — both the per-creator route
 * (ep10) and the cross-creator one (`ReviewDetectionsRequest`: `detections` is `min:1|max:200`).
 * Sending 201 or more is a 422, so a bulk approve larger than this is chunked into several requests.
 * This is the BE's per-request cap on `detections[]` for the review POSTs; the large-list modal's
 * "Approve selected" (chunked, manual multi-select) is chunked at this size too.
 */
export const BS_DETECTION_REVIEW_BATCH_SIZE = 200;

/**
 * Dual-purpose cap for the ep11 flag-occurrences endpoint: it is BOTH the page size the detections
 * GET returns per page AND the threshold above which the Bulk Resolve modal switches to the
 * large-list (approve-all / paged, "Review individually") experience. `data.total` still reports the
 * scope's TRUE match count, so a flag with more occurrences than this page size can never be resolved
 * from a single tile list — the modal pages through it (see the ep11 doc block below) instead of
 * rendering everything at once.
 */
export const BS_FLAG_OCCURRENCES_MAX_ITEMS = 15;

/** `?page=&pageSize=` for ep9 — omitted entirely (→ all flagged posts, no pagination) when neither is set. */
function buildReportQuery({ page, pageSize } = {}) {
  const params = [];
  if (page != null && page !== '') params.push(`page=${encodeURIComponent(page)}`);
  if (pageSize != null && pageSize !== '') params.push(`pageSize=${encodeURIComponent(pageSize)}`);
  return params.length ? `?${params.join('&')}` : '';
}

/**
 * `?cursor=&limit=` for the campaign flaggedPosts endpoint (doc #4). Only params that are set are appended;
 * a blank/`"0"`/`"null"` cursor is treated as "start from the beginning" (same as omitting it).
 */
function buildFlaggedPostsQuery({ cursor, limit } = {}) {
  const params = [];
  const blankCursor = cursor == null || cursor === '' || cursor === '0' || cursor === 'null';
  if (!blankCursor) params.push(`cursor=${encodeURIComponent(cursor)}`);
  if (limit != null && limit !== '') params.push(`limit=${encodeURIComponent(limit)}`);
  return params.length ? `?${params.join('&')}` : '';
}

/**
 * `?category=&label=&brand=&page=&pageSize=` for the flag-occurrences endpoints (ep11). A flag is
 * identified by the tuple `(category, label, brand)` ONLY — deliberately not by `source` or
 * `timeInSeconds`, so an "Apple TV" trademark hit in an image and one in a caption are the same flag.
 * Each flag param is emitted only when non-empty; `category` is the RAW backend category
 * (`detection.rawCategory`).
 *
 * PAGING IS AN ASSUMPTION, NOT A CONFIRMED CONTRACT: no BE change for it has landed yet, so we send
 * a 1-based `page` plus an explicit `pageSize`, mirroring what ep9 already takes (`buildReportQuery`).
 * This function is deliberately the ONLY place those names appear — if the BE settles on something
 * else (`offset`/`limit`, `perPage`, …) it is a one-line change here and nothing above it moves.
 * Confirm against the BE before release.
 *
 * Both params ride along only when `page` is a positive integer, so an unpaged call still produces
 * byte-for-byte the query it produced before paging existed.
 */
function buildFlagOccurrencesQuery({ category, label, brand, page, pageSize } = {}) {
  const params = [];
  const add = (name, value) => {
    const text = value == null ? '' : String(value).trim();
    if (text) params.push(`${name}=${encodeURIComponent(text)}`);
  };
  add('category', category);
  add('label', label);
  add('brand', brand);
  const pageNumber = Number(page);
  if (Number.isInteger(pageNumber) && pageNumber > 0) {
    const size = Number(pageSize);
    add('page', pageNumber);
    add('pageSize', size > 0 ? size : BS_FLAG_OCCURRENCES_MAX_ITEMS);
  }
  return params.length ? `?${params.join('&')}` : '';
}

export const BRAND_SAFETY_ENDPOINTS = {
  availability: '/creator/brand-safety/availability', // ep1
  run: '/creator/brand-safety/run', // ep2
  // doc #2 — campaign report section: one card per influencer (latest Succeeded check) + campaign rollup.
  creatorReports: (campaignId) => `/campaign/brand-safety/${campaignId}/creatorReports`,
  reviewPost: (creatorReportDetailsId, postTableId) =>
    `/creator/brand-safety/creator-report-details/${creatorReportDetailsId}/posts/${postTableId}`, // ep4 (PATCH)
  // doc #4 — campaign review section: campaign-wide summary + a FLAT, cursor-paginated feed of every
  // creator's flagged posts (cursor + limit).
  flaggedPosts: (campaignId, opts = {}) =>
    `/campaign/brand-safety/${campaignId}/flaggedPosts${buildFlaggedPostsQuery(opts)}`,
  creatorChecks: (creatorReportDetailsId) =>
    `/creator/brand-safety/creator-report-details/${creatorReportDetailsId}/checks`, // ep6 (superseded by `report` for the single-influencer report page)
  reviewPosts: '/creator/brand-safety/posts/reviews', // ep7 (bulk approve/reject, POST)
  flagConfig: '/creator/brand-safety/flag-config', // ep8 (persist Filters drawer params, POST)
  // ep9 — single-influencer report (lifetime aggregates + paginated flagged-only posts[]), replaces ep6
  // for BrandSafetyReportPage/Embed. `doApiCallGet` builds `host + endpoint`, so the query string appends here.
  report: (creatorReportDetailsId, opts = {}) =>
    `/creator/brand-safety/report/${creatorReportDetailsId}${buildReportQuery(opts)}`,
  // ep10 (AAPU-6775): per-detection approve/reject, POST. Scoped to ONE creator report; each item in
  // the body names its own `postTableId`, so one request spans every post of that creator.
  reviewDetections: (creatorReportDetailsId) =>
    `/creator/brand-safety/creator-report-details/${creatorReportDetailsId}/detections/reviews`,
  // ep10b — the same review, unscoped: every detection in the body carries its OWN
  // `creatorReportDetailsId`, so one request spans several creators. Used by the campaign-level Bulk
  // Resolve modal, whose occurrences are cross-creator by nature.
  reviewDetectionsAcrossCreators: '/creator/brand-safety/detections/reviews',
  // ep12 (AAPU-6775) — approve EVERY pending occurrence of one flag in a scope, with no detection
  // list. Two routes mirroring the ep11 read scopes; body is the same `{ category?, label?, brand? }`
  // filter the GET takes. See the contract block below.
  creatorApproveAllDetections: (creatorReportDetailsId) =>
    `/creator/brand-safety/creator-report-details/${creatorReportDetailsId}/detections/approve-all`,
  campaignApproveAllDetections: (campaignId) =>
    `/campaign/brand-safety/${campaignId}/detections/approve-all`,
  // ep11 (AAPU-6775) — every occurrence of ONE flag across a whole scope. Two routes, same
  // query + response; see the contract block below.
  // `paging` is `{ page, pageSize }` and is optional: omitting it asks for the unpaged (first page)
  // response, which is what the small-list modal and every non-modal caller want.
  creatorFlagOccurrences: (creatorReportDetailsId, flag = {}, paging = {}) =>
    `/creator/brand-safety/creator-report-details/${creatorReportDetailsId}/detections${buildFlagOccurrencesQuery(
      { ...flag, ...paging }
    )}`,
  campaignFlagOccurrences: (campaignId, flag = {}, paging = {}) =>
    `/campaign/brand-safety/${campaignId}/detections${buildFlagOccurrencesQuery({
      ...flag,
      ...paging,
    })}`,
};

// Availability scans available posts per creator (and a bulk selection multiplies that), so it needs a
// longer budget than the client's 30s default; anything past this aborts as a normal ERROR the modals handle.
export const AVAILABILITY_TIMEOUT_MS = 90000; // 90 seconds

// The campaign creatorReports (doc #2) and flaggedPosts (doc #4) endpoints aggregate a full rollup PER
// creator, so a large campaign legitimately takes longer than the client's 30s default — bump their budget
// to avoid a timeout abort surfacing as an error on the Media/Review tab.
export const CAMPAIGN_REPORT_TIMEOUT_MS = 90000; // 90 seconds

// ─── Documented request builders (eps 1 & 2) ──────────────────────────────────
// The doc's checkType is `full | content_count | time_range`, with `prohibited_unless`:
// `maxPostsPerInfluencer` is allowed ONLY with content_count, `startDate`/`endDate` ONLY with
// time_range. Sending an off-scope field is a 422, so the builders emit only the allowed fields.
export const BS_DOC_CHECK_TYPE = {
  FULL: 'full',
  CONTENT_COUNT: 'content_count',
  TIME_RANGE: 'time_range',
};

/** Only the scope fields the doc permits for this checkType. */
function scopeFields({ checkType, maxPostsPerInfluencer, startDate, endDate } = {}) {
  if (checkType === BS_DOC_CHECK_TYPE.CONTENT_COUNT) {
    return maxPostsPerInfluencer != null
      ? { maxPostsPerInfluencer: Number(maxPostsPerInfluencer) }
      : {};
  }
  if (checkType === BS_DOC_CHECK_TYPE.TIME_RANGE) {
    const fields = {};
    if (startDate) fields.startDate = startDate;
    if (endDate) fields.endDate = endDate;
    return fields;
  }
  return {};
}

/**
 * Ep 1 availability body (BE-confirmed shape): an object with a shared `checkType` + scope and a
 * `creators` array of `{ username, socialNetwork }`. The endpoint accepts both a single creator (an
 * array of 1) and a bulk selection; the response carries an aggregate `credits` block.
 */
export function buildAvailabilityBody({
  checkType = BS_DOC_CHECK_TYPE.FULL,
  creators = [],
  maxPostsPerInfluencer,
  startDate,
  endDate,
} = {}) {
  return {
    checkType,
    ...scopeFields({ checkType, maxPostsPerInfluencer, startDate, endDate }),
    creators: (Array.isArray(creators) ? creators : []).map((creator) => ({
      username: creator.username,
      socialNetwork: creator.socialNetwork,
    })),
  };
}

/**
 * Ep 2 run body — a batch of `creators` plus the shared scope. Each creator is keyed by the same
 * handle the availability endpoint uses (`username` + `socialNetwork`) plus its `creatorReportDetailsId`.
 */
export function buildRunBody({
  checkType = BS_DOC_CHECK_TYPE.FULL,
  creators = [],
  maxPostsPerInfluencer,
  startDate,
  endDate,
} = {}) {
  return {
    checkType,
    ...scopeFields({ checkType, maxPostsPerInfluencer, startDate, endDate }),
    creators: (Array.isArray(creators) ? creators : []).map((creator) => ({
      username: creator.username,
      socialNetwork: creator.socialNetwork,
      creatorReportDetailsId: creator.creatorReportDetailsId ?? creator.crd ?? null,
    })),
  };
}

/**
 * Ep 10 (AAPU-6775) body — per-detection review, live on the BE (`BrandSafetyCheckController::
 * reviewPostDetections`, validated by `ReviewDetectionsRequest`). Each detection in the body names
 * its OWN `postTableId`, so a single request resolves flagged items across several posts.
 *
 * The report payload carries no detection id, so a detection has to be located server-side by the
 * tuple that uniquely identifies it inside the post's `safetyDetails`:
 *
 *   `postTableId`    which post the item lives on (required, integer)
 *   `key`            the FE's stable per-detection key, echoed back verbatim in `reviewed`/`failed`
 *   `source`         which section the hit came from — 'image' | 'text' | 'video' (required)
 *   `category`       the RAW backend flag category (e.g. 'Smoking', 'Trademark'), not the display label (required)
 *   `label`          the detected object / mark label, or null
 *   `brand`          the trademark brand, or null for a non-trademark flag
 *   `timeInSeconds`  the video segment's start, or null for image/text hits
 *
 * Two routes share this body, differing only in how the creator is named:
 *
 *   per-creator   POST .../creator-report-details/{creatorReportDetailsId}/detections/reviews
 *                 the crd is in the PATH; body detections carry none.
 *   cross-creator POST /creator/brand-safety/detections/reviews          (`includeCreator: true`)
 *                 no crd in the path; EVERY body detection carries its own `creatorReportDetailsId`.
 *                 Mirrors the post-level `POST /creator/brand-safety/posts/reviews`, and is what the
 *                 campaign-level Bulk Resolve modal uses — its occurrences span creators.
 *
 * Request:  { action: 'approved'|'rejected',
 *             detections: [{ postTableId, key, source, category, label, brand, timeInSeconds,
 *                            creatorReportDetailsId? }] }
 * Response: { data: {
 *               reviewed: [key, …],
 *               failed: [{ key, reason: 'post_not_analyzed'|'save_failed'|'creator_not_visible' }],
 *               autoApprovedPostTableIds: [int, …],
 *            } }
 *
 * `creator_not_visible` only occurs on the cross-creator route (a crd outside the workspace); the
 * per-creator route resolves visibility from the path and 404s instead.
 *
 * `autoApprovedPostTableIds` is the BE telling us which posts it flipped to the same verdict because
 * this request resolved their last outstanding flagged item. It is AUTHORITATIVE — the slice applies
 * it on top of (not instead of) its own optimistic "every item on the post is now resolved" rule,
 * which only ever sees the items the client happens to have loaded.
 *
 * `detections` is capped at `BS_DETECTION_REVIEW_BATCH_SIZE` (200) items and must hold at least 1 —
 * 201 or more is a 422 — so a bulk approve is chunked at 200 (and, on the per-creator route, bucketed
 * by `creatorReportDetailsId` first); see `resolveReviewDetectionsBatch` in the slice. A flag with
 * more occurrences than fit in one page still comes through here when the reviewer picks tiles
 * individually across pages — that selection is simply chunked; "Approve all" skips this route.
 *
 * There is NO `postAction` and no top-level `postTableId` on the response.
 *
 * @param {object} args
 * @param {'approved'|'rejected'} args.action
 * @param {Array<{ postTableId: number|string, creatorReportDetailsId?: number|string, detection: object }>}
 *        args.items  one entry per flagged item, pairing its post with the normalized detection
 *        (see `normalizeReviewPost`)
 * @param {boolean} [args.includeCreator=false]  emit each detection's own `creatorReportDetailsId`
 *        (the cross-creator route)
 */
export function buildDetectionReviewBody({ action, items = [], includeCreator = false } = {}) {
  return {
    action,
    detections: (Array.isArray(items) ? items : []).map(
      ({ postTableId, creatorReportDetailsId, detection = {} }) => ({
        postTableId: Number(postTableId),
        ...(includeCreator ? { creatorReportDetailsId: Number(creatorReportDetailsId) } : {}),
        key: detection.key,
        source: detection.source ?? null,
        category: detection.rawCategory ?? null,
        label: detection.label ?? null,
        brand: detection.brand ?? null,
        timeInSeconds: detection.start ?? null,
      })
    ),
  };
}

/**
 * Ep 12 (AAPU-6775) body — approve EVERY pending occurrence of one flag in a scope, with no detection
 * list. Same `(category, label, brand)` filter the ep11 GET takes, so the "Approve all N" button can
 * be built from exactly what the modal already fetched with.
 *
 * Only non-blank keys are emitted (a blank is a wildcard, not a "match empty"), and the BE 422s when
 * all three are missing — approving *every* flag in a scope is deliberately not expressible here.
 * There is no `action`: this route only ever approves.
 *
 * Request:  POST .../creator-report-details/{crd}/detections/approve-all
 *           POST /campaign/brand-safety/{campaignId}/detections/approve-all
 *           { category?, label?, brand? }
 * Response: { data: { flag: { category, label, brand },
 *                     matched, approved, alreadyApproved, failed,
 *                     autoApprovedPostTableIds: [int, …] } }
 *
 * Already-approved occurrences are skipped (counted in `alreadyApproved`, not re-written). Throttled
 * at 30 requests/minute, behind the same brand-safety-run permission as the other write endpoints; a
 * cross-tenant id is a 404.
 *
 * @param {object} args
 * @param {string} [args.category]  the RAW backend flag category, not the display label
 * @param {string} [args.label]
 * @param {string} [args.brand]
 */
export function buildApproveAllBody({ category, label, brand } = {}) {
  const body = {};
  const add = (name, value) => {
    const text = value == null ? '' : String(value).trim();
    if (text) body[name] = text;
  };
  add('category', category);
  add('label', label);
  add('brand', brand);
  return body;
}

/**
 * Ep 11 (AAPU-6775) — every occurrence of ONE flag across a whole scope. Both routes are live in the BE;
 * this is the contract both sides code against. It backs the repurposed "Bulk Approve" modal:
 * the reviewer clicks Bulk Approve on a finding card (say the Trademark card for "Apple TV") and the
 * modal lists that same flag on EVERY post in scope, one tile per occurrence.
 *
 * Request (GET, two scopes, identical query + response):
 *   creator  — GET /creator/brand-safety/creator-report-details/{crd}/detections{query}
 *              scope = every flagged post of that creator report (the whole single-influencer report)
 *   campaign — GET /campaign/brand-safety/{campaignId}/detections{query}
 *              scope = every flagged post of every creator in the campaign
 *
 * Query (see `buildFlagOccurrencesQuery`): `?category=&label=&brand=&page=&pageSize=`, each sent only
 * when non-empty and URL-encoded.
 *   `category`  the RAW backend flag category ('Trademark', 'Smoking', …), NOT the display label
 *   `label`     the detected object / mark label
 *   `brand`     the trademark brand, omitted for a non-trademark flag
 *   `page`      1-based page index — ASSUMED, see below
 *   `pageSize`  items per page, `BS_FLAG_OCCURRENCES_MAX_ITEMS` (15) — ASSUMED, see below
 *
 * The two paging params are an ASSUMPTION the BE has not confirmed: they mirror ep9's
 * `?page=&pageSize=` and are emitted from one place (`buildFlagOccurrencesQuery`) so renaming them is
 * a one-line change. Both are omitted unless a page is asked for, so the unpaged request is unchanged.
 *
 * Matching rule: a detection matches when all three SUPPLIED params match, compared trimmed and
 * case-insensitively (the same normalization `detectionIdentityKey` applies). An omitted param is a
 * wildcard. `source` and `timeInSeconds` are deliberately NOT part of the identity — an "Apple TV"
 * trademark in an image and one in a caption are the same flag and must both come back.
 *
 * Scope rules: flagged posts only (an unflagged post cannot carry the flag), across ALL pages of the
 * report/feed regardless of what the client has loaded, and INCLUDING posts already reviewed — the
 * modal renders those as read-only "Approved"/"Rejected" tiles so the reviewer sees the whole picture.
 * Ordering: pending occurrences first, then by `publishDate` descending, so the actionable tiles lead.
 * Pagination: `items` holds one page of at most `BS_FLAG_OCCURRENCES_MAX_ITEMS` (15) occurrences,
 * while `data.total` always reports the scope's TRUE count — so `total` drives the page count and
 * `items` is never a "sample" the reviewer can't reach past. The FE branches on `total` alone:
 *
 *   total <= 15  the whole flag fits in the first page's `items[]`. Render one selectable tile per
 *                occurrence, no pager, and approve the ticked ones through the review route.
 *   total > 15   the large-list experience: "Approve all N" (approve-all route) by default, or
 *                "Review individually" to select tiles page-by-page (15 at a time, the pager
 *                refetching each page), chunking the selected approvals at
 *                `BS_DETECTION_REVIEW_BATCH_SIZE` (200) per review request.
 *
 * `data.page` echoes the page served; the FE falls back to the page it asked for when the field is
 * absent or not finite, and `total` likewise falls back to `items.length`.
 *
 * Response:
 *   { data: {
 *       flag: { category, label, brand },
 *       total: number,                              // true match count across the scope
 *       pendingApprovalCount: number,               // how many of `total` are still pending — the
 *                                                   // count "Approve all" acts on (large lists)
 *       page: number,                               // ASSUMED echo of the requested page (1-based)
 *       items: [{                                   // one page, at most BS_FLAG_OCCURRENCES_MAX_ITEMS
 *         detectionKey,                             // sha-256 of the detection tuple, BE-computed
 *         creatorReportDetailsId, postTableId,
 *         postType: 'IMAGE'|'VIDEO', postMediaUrl, postThumbnailUrl, postUrl, publishDate,
 *         creatorUsername, socialNetwork,
 *         source: 'image'|'text'|'video', category, label, brand, timeInSeconds|null,
 *         severityLevel: 'L1'|'L2'|'L3'|'L4'|null,   // raw ML level, same as everywhere else
 *         confidence|null,
 *         thumbnailUrl|null,                        // per-detection crop, same as the report payload
 *         reviewAction: 'approved'|'rejected'|null, // stored verdict for THIS occurrence
 *         postAction: 'approved'|'rejected'|null    // stored post-level verdict
 *       }]
 *   } }
 *
 * `detectionKey` is carried through as `detection.detectionKey`; the FE's own composite
 * `detection.key` stays the review overlay's identity (see `normalizeFlagOccurrences`).
 *
 * This endpoint is READ-ONLY. The two write paths out of the modal:
 *   total <= 15  the review route — `POST .../creator-report-details/{crd}/detections/reviews` for a
 *                creator-scoped modal, `POST /creator/brand-safety/detections/reviews` for a
 *                campaign-scoped one (its occurrences span creators). See `buildDetectionReviewBody`.
 *   total > 15   by default the approve-all route — `POST .../detections/approve-all` (creator) or
 *                `POST /campaign/brand-safety/{campaignId}/detections/approve-all`, carrying the same
 *                `(category, label, brand)` filter this GET took (see `buildApproveAllBody`); or, if
 *                the reviewer switches to "Review individually", the same review route as above with
 *                the tiles they ticked across pages, chunked at `BS_DETECTION_REVIEW_BATCH_SIZE`.
 *
 * The response is normalized by `normalizeFlagOccurrences` in `brandSafetyUtils.js`.
 */

// Promise wrappers over the callback-based client, matching the Audiences slice idiom.
export const apiGet = (endpoint, options = {}) =>
  new Promise((resolve) => arClient.doApiCallGet(endpoint, resolve, options));

export const apiPost = (endpoint, body = {}, options = {}) =>
  new Promise((resolve) => arClient.doApiCallV2(endpoint, body, resolve, options));

// `doApiCallV2` hardcodes POST; only the generic `doApiCall` honours `options.method`, so PATCH goes here.
export const apiPatch = (endpoint, body = {}) =>
  new Promise((resolve) =>
    arClient.doApiCall(endpoint, body, resolve, undefined, { method: 'PATCH' })
  );
